import api from './api';

// API client for the manual "Option Selling" feature (ATM±N CE+PE strangle, BUY-hedge-first).
// Kept in its own file, same convention as spreadApi.ts.

export type OptionSellingMarket = 'NIFTY' | 'SENSEX';

const marketPath = (market: OptionSellingMarket) => (market === 'NIFTY' ? 'nifty' : 'sensex');

export interface PlaceStrangleRequest {
  expiryDate: string; // 'YYYY-MM-DD'
  sellOffset: number;
  hedgeDistanceRupees: number;
  // Client's explicit ask: replaces the old flat Lots field — quantity is sized from this % of
  // the configured Capital Amount (Broker Management) against the SELL CE leg's LTP, same
  // formula Nifty Buying already uses. Applied to both SELL legs (CE+PE) and their hedges.
  capitalPercentage: number;
  slippagePercentage: number;
  delaySeconds?: number;
  slX?: number;
  slY?: number;
  slX2?: number;
  slY2?: number;
  // OR-alternative per pair, checked against the live X%/Y% average-move metric instead of the
  // X/Y count.
  slXPct?: number;
  slYPct?: number;
  slX2Pct?: number;
  slY2Pct?: number;
  slAmount?: number;
  targetPctFromPeak?: number;
  // When true, this batch is auto-exited once the X/Y breadth condition is hit. When false
  // (default), the SL fields are reference-only and nothing triggers automatically.
  slEnabled?: boolean;
  broker?: string; // 'DHAN' (default) | 'GROWW' | ... — see Broker Management
}

// Nifty and Sensex are independent panels on the screen, each with their own full set of
// values — settings are remembered per market, not once per user.
export interface OptionSellingSettings {
  market: OptionSellingMarket;
  expiryDate: string | null;
  sellOffset: number;
  hedgeDistanceRupees: number;
  capitalPercentage: number;
  slippagePercentage: number;
  delaySeconds: number;
  slEnabled: boolean;
  slX1a: number; slX1b: number;
  slY1a: number; slY1b: number;
  slX2a: number; slX2b: number;
  slY2a: number; slY2b: number;
  slAmount: number;
  targetPctFromPeak: number;
  broker?: string;
}

export type OptionSellingSettingsByMarket = Record<OptionSellingMarket, OptionSellingSettings | null>;

export const optionSellingAPI = {
  placeOrder: (market: OptionSellingMarket, payload: PlaceStrangleRequest) =>
    api.post(`/option-selling/${marketPath(market)}/place`, payload),
  exitPosition: (market: OptionSellingMarket, batchId: string) =>
    api.post(`/option-selling/${marketPath(market)}/${batchId}/exit`),
  exitAll: (market: OptionSellingMarket) =>
    api.post(`/option-selling/${marketPath(market)}/exit-all`),
  getOpenPositions: (market: OptionSellingMarket) =>
    api.get(`/option-selling/${marketPath(market)}/positions/open`),
  getOrderHistory: (market: OptionSellingMarket, page = 1, limit = 50) =>
    api.get(`/option-selling/${marketPath(market)}/orders?page=${page}&limit=${limit}`),
  clearOrderHistory: (market: OptionSellingMarket) =>
    api.delete(`/option-selling/${marketPath(market)}/orders/clear`),
  deleteOrdersPermanently: (market: OptionSellingMarket) =>
    api.delete(`/option-selling/${marketPath(market)}/orders/delete-permanently`),
  // Remembers the last-used screen values per market (not a triggerable config — see backend
  // comment). getSettings returns both markets' settings in one call.
  getSettings: () => api.get<OptionSellingSettingsByMarket>('/option-selling/settings'),
  saveSettings: (market: OptionSellingMarket, settings: Partial<OptionSellingSettings>) =>
    api.put('/option-selling/settings', { market, ...settings }),
};
